import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, supabaseUrl, supabaseAnonKey } from '../lib/supabase';

const BookingContext = createContext(null);

// Holds all booking state shared across routes. State is in memory only —
// a page refresh resets the flow by design (no localStorage / sessionStorage).
export function BookingProvider({ children }) {
  const navigate = useNavigate();

  // Customer details
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [customerId, setCustomerId] = useState(null);

  // Booking selections
  const [selectedDate, setSelectedDate] = useState(null); // ISO date (YYYY-MM-DD) from the date cards, or null
  const [passengers, setPassengers] = useState({ adults: 1, children: 0, infants: 0 });
  const [availabilityCruises, setAvailabilityCruises] = useState([]); // live availability result
  const [selectedCruise, setSelectedCruise] = useState(null); // full cruise-card object
  const [paxDetails, setPaxDetails] = useState([]);           // per-passenger detail rows
  const [agreedTnC, setAgreedTnC] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  // Rupees actually charged for the current order — the server-computed
  // Razorpay order amount returned by create-order, not the display estimate.
  // Read by the Confirmation screen for the Meta Purchase value.
  const [paidAmount, setPaidAmount] = useState(null);

  // Booking hold — the live reservation placed by the `hold` edge function.
  // holdExpiresAt is the absolute expiry (ISO) returned by the backend; the
  // countdown below is derived from it so it stays correct across every screen.
  const [holdId, setHoldId] = useState(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [holdSeconds, setHoldSeconds] = useState(0);
  const [holdExpiredNotice, setHoldExpiredNotice] = useState(false);

  // Cross-screen fade — preserves the original 250ms step transition
  const [fading, setFading] = useState(false);

  // Drive the countdown from the absolute expiry. When it reaches zero the hold
  // is cleared and the guest is routed back to Available Cruises (date + party
  // preserved). No release call is needed — the DB treats an expired hold as
  // free and the next create releases any prior hold.
  useEffect(() => {
    if (!holdExpiresAt) {
      setHoldSeconds(0);
      return;
    }
    const expMs = new Date(holdExpiresAt).getTime();
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expMs - Date.now()) / 1000));
      setHoldSeconds(remaining);
      return remaining;
    };
    const expire = () => {
      setHoldExpiredNotice(true);
      setHoldId(null);
      setHoldExpiresAt(null);
      setSelectedCruise(null);
      navigate('/availability');
    };
    if (tick() <= 0) {
      expire();
      return;
    }
    const timer = setInterval(() => {
      if (tick() <= 0) {
        clearInterval(timer);
        expire();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [holdExpiresAt, navigate]);

  // Navigate with the original fade-out, applying the same state resets the
  // old step machine performed when entering certain steps.
  const go = useCallback((path) => {
    setFading(true);
    setTimeout(() => {
      if (path === '/select-date') setSelectedDate(null);
      if (path === '/availability') setSelectedCruise(null);
      navigate(path);
      setFading(false);
    }, 250);
  }, [navigate]);

  // Leave the booking flow entirely and release THIS customer's hold. Used by
  // every flow-EXIT control (cancel links, the CruiseSummary "Leave" confirm,
  // the under-2-minutes reselect). Inter-step navigation keeps using go() and
  // never calls this, so stepping back to edit preserves the hold.
  //
  // Resilient + non-blocking: the release is fired and NOT awaited (the hold is
  // customer-scoped server-side), local hold state is cleared, and we navigate
  // regardless of whether the release call succeeds — the hold's own expiry is
  // the real backstop.
  const exitFlow = useCallback(() => {
    try {
      if (customerId) {
        supabase.functions
          .invoke('hold', { body: { action: 'release', customerId } })
          .catch((err) => console.error('Hold release on flow exit failed (will expire on its own):', err));
      }
    } catch (err) {
      console.error('Hold release on flow exit failed (will expire on its own):', err);
    }
    setHoldId(null);
    setHoldExpiresAt(null);
    go('/availability');
  }, [customerId, go]);

  // Best-effort hold cleanup when the whole page goes away (tab close / refresh /
  // external navigation) while a hold is active. beforeunload only raises the
  // browser's native "leave site?" prompt — it does NOT release, so cancelling
  // the prompt keeps the hold. The actual release is sent from pagehide, which
  // fires only when the document is genuinely being unloaded (so a cancelled
  // prompt never releases). SPA route changes don't fire pagehide, so moving
  // between booking steps never triggers this. All best-effort — expiry backstops.
  useEffect(() => {
    if (!holdId) return;

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // required for the native prompt in most browsers
    };

    const onPageHide = () => {
      if (!customerId) return;
      try {
        const url = `${supabaseUrl}/functions/v1/hold`;
        const payload = JSON.stringify({ action: 'release', customerId });
        // fetch+keepalive lets us send the apikey/authorization headers the
        // functions gateway requires (sendBeacon can't set headers) while still
        // surviving the unload. Failures are ignored — best-effort only.
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: payload,
        }).catch(() => {});
      } catch (_) {
        /* ignore — best effort only */
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [holdId, customerId]);

  const value = {
    // customer details
    fullName, setFullName,
    phone, setPhone,
    email, setEmail,
    isVerified, setIsVerified,
    customerId, setCustomerId,

    // selections
    selectedDate, setSelectedDate,
    passengers, setPassengers,
    availabilityCruises, setAvailabilityCruises,

    // selected cruise — stored as one object, exposed under the spec's names
    selectedCruise, setSelectedCruise,
    selectedCruiseData: selectedCruise,
    selectedCruiseType: selectedCruise ? selectedCruise.cruiseName : null,
    selectedDeck: selectedCruise ? selectedCruise.deckLabel : null,

    // per-passenger details (a.k.a. passengerList)
    paxDetails, setPaxDetails,
    passengerList: paxDetails, setPassengerList: setPaxDetails,

    // terms agreement
    agreedTnC, setAgreedTnC,
    agreedToTerms: agreedTnC, setAgreedToTerms: setAgreedTnC,

    // confirmation
    bookingRef, setBookingRef,
    paidAmount, setPaidAmount,

    // booking hold + transition
    holdId, setHoldId,
    holdExpiresAt, setHoldExpiresAt,
    holdSeconds,
    holdExpiredNotice, setHoldExpiredNotice,
    fading,
    go,
    exitFlow,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error('useBooking must be used within a BookingProvider');
  return ctx;
}
